import { useEffect, useRef, useState } from 'react';
import Box from '@mui/system/Box';
import TextField from '@mui/material/TextField';
import { SVGS } from '../../assets';
import { COUNTRIES_DATA, CountryObject } from '@zonofi/i18n';
import { Divider } from '../../components';
import { TouchableOpacity } from '../../common';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import './country-list-modal.scss';
import { commonInputSx } from '../../helper';

interface CountryListModalProps {
  open: boolean;
  onClose: () => void;
  onCountrySelect?: (item: CountryObject) => void;
  currentCountry?: CountryObject;
}

export const CountryListModal: React.FC<CountryListModalProps> = ({
  open,
  onClose,
  onCountrySelect,
  currentCountry,
}) => {
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [searchText, setSearchText] = useState('');
  const [countryData, setCountryData] =
    useState<CountryObject[]>(COUNTRIES_DATA);

  useEffect(() => {
    if (!open || !currentCountry) return;

    requestAnimationFrame(() => {
      const node = itemRefs.current[currentCountry.code];
      node?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    });
  }, [open, currentCountry]);

  const handleSearch = (value: string) => {
    setSearchText(value);

    const query = value.toLowerCase();
    if (!query) {
      setCountryData(COUNTRIES_DATA);
      return;
    }

    const filtered = COUNTRIES_DATA.filter((c) => {
      return (
        c.name.toLowerCase().includes(query) ||
        c.code.toLowerCase().includes(query) ||
        c.dial_code.includes(query)
      );
    });
    setCountryData(filtered);
  };

  const handleClose = () => {
    setSearchText('');
    setCountryData(COUNTRIES_DATA);
    onClose?.();
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      onOpen={() => {}}
      PaperProps={{
        className: 'country-list__drawer',
      }}
      disableSwipeToOpen
    >
      <Box className="country-list__container">
        <div className="search-view">
          <TextField
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            sx={commonInputSx}
            placeholder={'Search'}
            InputProps={{
              startAdornment: (
                <img
                  src={SVGS.SearchIcon}
                  width={18}
                  height={18}
                  className="search-view-search"
                />
              ),
            }}
            autoFocus
          />
        </div>

        <Divider />

        <div className="country-list">
          {countryData?.map((item) => {
            return (
              <TouchableOpacity
                key={item?.code}
                containerRef={(el) => {
                  itemRefs.current[item.code] = el;
                }}
                className={`country-list__view ${
                  currentCountry?.code === item?.code
                    ? 'country-list__view--selected'
                    : ''
                }`}
                onPress={() => {
                  onCountrySelect?.(item);
                  handleClose();
                }}
              >
                <div className="country-list__view--text font-regular">
                  {`${item?.flag} ${item?.name} (${item.dial_code})`}
                </div>
                <img
                  src={
                    currentCountry?.code === item?.code
                      ? SVGS.CircleSelected
                      : SVGS.CircleUnselected
                  }
                  width={20}
                  height={20}
                />
              </TouchableOpacity>
            );
          })}
        </div>
      </Box>
    </SwipeableDrawer>
  );
};
